import MarkdownIt from 'markdown-it';
import anchor from 'markdown-it-anchor';
import hljs from 'highlight.js';
import DOMPurify from 'dompurify';

// RenderRule type extracted from the renderer rules record, avoiding MarkdownIt namespace reference
type RenderRule = NonNullable<MarkdownIt['renderer']['rules'][string]>;

const md: MarkdownIt = MarkdownIt({
  html: true,
  linkify: true,
  breaks: false,
  typographer: false,
  highlight: (code, lang) => {
    if (lang && hljs.getLanguage(lang)) {
      try {
        const result = hljs.highlight(code, { language: lang, ignoreIllegals: true });
        return `<pre><code class="hljs language-${lang}">${result.value}</code></pre>`;
      } catch {
        // 降级到无高亮
      }
    }
    const escaped = md.utils.escapeHtml(code);
    return `<pre><code class="hljs">${escaped}</code></pre>`;
  },
});

md.use(anchor, {
  level: [1, 2, 3],
  permalink: false,
  slugify: (s: string) =>
    s
      .toLowerCase()
      .trim()
      .replace(/[\s]+/g, '-')
      .replace(/[^一-龥a-z0-9-]/g, ''),
});

const defaultLinkOpen: RenderRule =
  md.renderer.rules.link_open ??
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  const href = token.attrGet('href') ?? '';
  if (/^https?:\/\//.test(href)) {
    token.attrSet('target', '_blank');
    token.attrSet('rel', 'noopener noreferrer');
  }
  return defaultLinkOpen(tokens, idx, options, env, self);
};

export function renderMarkdown(source: string): string {
  // 启用 markdown-it 的 html 选项后，inline HTML（<table>/<div align>/<details> 等）
  // 直接进入产物，必须由 DOMPurify 过滤掉 <script>/on*/javascript: 等危险节点。
  // target 默认会被 DOMPurify 剥掉防 tabnabbing，但我们已经显式输出了 rel="noopener noreferrer"，
  // 所以白名单加回 target，以保留 link_open 规则注入的 target="_blank"。
  return DOMPurify.sanitize(md.render(source), { ADD_ATTR: ['target'] });
}
